import React from "react";
// eslint-disable-next-line
import { MouseEvent, useEffect, useMemo, useState } from "react";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import {
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  CloseOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableTable from "../../../../../components/ReusableComponent/ReusableTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { CompanyReport } from "../../../../../types/companies-managment/CompanyReport";
import AddEditCompanyReport from "./AddEditCompanyReport";
import ExpandingDetail from "./ExpandingDetail";

// ==============================|| COMPANY reportS LIST ||============================== //

const CompanyReports = () => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CompanyReport | null>(null);
  const [selected, setSelected] = useState<any>("");
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<CompanyReport>[]>(
    () => [
      {
        header: () => <FormattedMessage id="ID" />,
        accessorKey: "id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="Resume ID" />,
        accessorKey: "resume_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">{row.original.resume_id}</Typography>
        ),
      },
      {
        header: () => <FormattedMessage id="report candidate" />,
        accessorKey: "report_candidate_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.report_candidate?.text_en}
          </Typography>
        ),
      },
      //report_candidate_id
      {
        header: () => <FormattedMessage id="Job Seeker Name" />,
        accessorKey: "job_seeker_name",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.job_seeker_name}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Company Name (EN)" />,
        accessorKey: "company_profile.name_en",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Typography variant="subtitle2">
            {row.original.company_profile?.name_en}
          </Typography>
        ),
      },

      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: { className: "cell-center" },
        disableSortBy: true,
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined
              style={{
                color: theme.palette.error.main,
              }}
            />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row" spacing={0}>
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setSelectedCustomer(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e: MouseEvent<HTMLButtonElement>) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    // eslint-disable-next-line
    [theme, expandedRowId]
  );
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CompanyReport[]>([]);
  const [countPage, setCountPage] = useState({
    current_page: 0,
    last_page: 0,
  });
  const [currentPage, setCurrenttPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchedtext, setSearchedtext] = useState("");
  const [loadingSearsh, setloadingSearsh] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/company_reports/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Company report deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete company report",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(
        `/company_reports?page=${currentPage}${
          searchQuery ? `&title=${searchQuery}` : ""
        }`
      );
      const { data, meta } = response.data;
      setResult(data);
      setCountPage(meta);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching company reports",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, searchQuery]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrenttPage(page);
  };

  const handleSearch = () => {
    setloadingSearsh(true);
    setCurrenttPage(1);
    setSearchQuery(searchedtext);
    setloadingSearsh(false);
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <ReusableTable
          data={result}
          columns={columns}
          countPage={countPage}
          handlePageChange={handlePageChange}
          handleSearchChange={handleSearch}
          currentPage={currentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setCurrenttPage={setCurrenttPage}
          loadingSearsh={loadingSearsh}
          searchedtext={searchedtext}
          setSearchedtext={setSearchedtext}
          modalToggler={() => {
            setCustomerModal(true);
            setSelectedCustomer(null);
          }}
          renderDetailPanel={({ row }: any) => (
            <ExpandingDetail data={row.original} />
          )}
          expandedRowId={expandedRowId}
          setExpandedRowId={setExpandedRowId}
        />
      )}

      <ReusableDeleteDialog
        title={selected?.name}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <AddEditCompanyReport
        open={customerModal}
        setResult={setResult}
        loading={formLoading}
        setLoading={setFormLoading}
        fetchData={result}
        id={selected}
        companyReport={selectedCustomer}
        handleClose={() => setCustomerModal(false)}
      />
    </>
  );
};

export default CompanyReports;
